import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
} from "@mui/material";
import { useEffect } from "react";
import FormTextField from "../../../components/elements/FormTextField";
import { defaultCustomerConfig } from "../../../config/config";
import { Customer } from "../../../data/models/customers/CustomerModel";
import { departments, municipalities } from "../../../utils/OtherOptions";
import { useFormWithNavigation } from "../../hooks/useFormWithNavigation";

interface Props {
  open: boolean;
  onClose: () => void;
  initialValue?: Customer;
  onSave: (
    data: Omit<Customer, "id"> | Partial<Omit<Customer, "id">>
  ) => Promise<void> | void;
  loading: boolean;
}

export interface PersonTypeItem {
  value: string;
  id: string; // data-id
}

export const _personTypes: PersonTypeItem[] = [
  { value: "PERSONA NATURAL", id: "2" },
  { value: "PERSONA JURIDICA", id: "1" },
];

const _documentTypes = [
  ["CEDULA DE CIUDADANIA", "13"],
  ["NIT", "31"],
  ["CEDULA DE EXTRANJERIA", "22"],
  ["REGISTRO CIVIL", "11"],
  ["TARJETA DE IDENTIDAD", "12"],
  ["TARJETA DE EXTRANJERIA", "21"],
  ["PASAPORTE", "41"],
  ["DOCUMENTO DE IDENTIFICACION EXTRANJERO", "42"],
  ["PEP (PERMISO ESPECIAL DE PERMANENCIA)", "47"],
  ["PPT (PERMISO PROTECCION TEMPORAL)", "48"],
  ["NIT DE OTRO PAIS", "50"],
  ["NUIP", "91"],
];

export default function CreateEditCustomerDialog({
  open,
  onClose,
  initialValue,
  onSave,
  loading,
}: Props) {
  const initial: Omit<Customer, "id"> = {
    fullName: "",
    documentType: "",
    documentTypeId: "",
    documentNumber: "",
    personType: "",
    personTypeId: "",
    phone: defaultCustomerConfig.phone,
    email: defaultCustomerConfig.email,
    address: defaultCustomerConfig.address,
    department: defaultCustomerConfig.department,
    departmentId: defaultCustomerConfig.departmentId,
    city: defaultCustomerConfig.city,
    cityId: defaultCustomerConfig.cityId,
    neighborhood: defaultCustomerConfig.neighborhood,
    notes: "",
  };

  const validate = (
    field: keyof typeof initial,
    value: any,
    form: typeof initial
  ) => {
    if (
      [
        "fullName",
        "documentType",
        "documentTypeId",
        "documentNumber",
        "personType",
        "phone",
        "email",
        "address",
        "department",
        "departmentId",
        "city",
        "cityId",
        "neighborhood",
      ].includes(field) &&
      !String(value).trim()
    ) {
      return "Campo obligatorio";
    }

    if (field === "phone") {
      const clean = String(value).replace(/\D/g, "");
      if (!clean) return "Campo obligatorio";
      if (clean.length < 7) return "Número demasiado corto";
      if (clean.length > 15) return "Número demasiado largo";
      if (!/^\d+$/.test(clean)) return "Solo se permiten números";
    }

    if (field === "email") {
      const email = String(value).trim();
      const regex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
      if (!email) return "Campo obligatorio";
      if (!regex.test(email)) return "Email inválido";
    }

    if (field === "documentNumber") {
      const clean = String(value).trim();
      if (!/^\d+$/.test(clean)) {
        return "Solo se permiten números";
      }
      if (form.documentTypeId === "31") {
        if (clean.length > 15) {
          return "El NIT no puede tener más de 15 dígitos";
        }
      }
      if (clean.length < 5) {
        return "Número de documento inválido";
      }
    }

    return "";
  };

  const {
    form,
    setForm,
    errors,
    refs,
    handleChange,
    handleEnter,
    validateAll,
  } = useFormWithNavigation(initial, validate, { autofocus: true });

  const _departments = departments.map(([code, name]) => ({
    id: code,
    name,
  }));

  const _municipalities = municipalities.map(([code, name]) => ({
    id: code,
    name,
  }));

  useEffect(() => {
    if (open && !initialValue) {
      setForm(initial);
      setTimeout(() => {
        const firstKey = Object.keys(refs)[0] as keyof typeof initial;
        refs[firstKey]?.current?.focus();
      }, 50);
    }
  }, [open, initialValue]);

  useEffect(() => {
    if (open && initialValue) {
      const { id, ...rest } = initialValue;
      setForm(rest);
    }
  }, [open, initialValue]);

  useEffect(() => {
    if (!form.documentType) return;
    const found = _documentTypes.find((d) => d[0] === form.documentType);
    if (found) {
      setForm((prev) => ({ ...prev, documentTypeId: found[1] }));
    }
  }, [form.documentType]);

  useEffect(() => {
    if (!form.department) return;
    const found = _departments.find((d) => d.name === form.department);
    if (found) {
      setForm((prev) => ({ ...prev, departmentId: found.id }));
    }
  }, [form.department]);

  useEffect(() => {
    if (!form.city) return;
    const found = _municipalities.find((m) => m.name === form.city);
    if (found) {
      setForm((prev) => ({ ...prev, cityId: found.id }));
    }
  }, [form.city]);

  useEffect(() => {
    if (!form.personType) return;
    const found = _personTypes.find((p) => p.value === form.personType);
    if (found) {
      setForm((prev) => ({
        ...prev,
        personTypeId: found.id,
      }));
    }
  }, [form.personType]);

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!validateAll()) return;
    await onSave(form);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {initialValue ? "Editar cliente" : "Nuevo cliente"}
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={2}>
            {/* FULL NAME */}
            <Grid item xs={12} sm={6}>
              <FormTextField
                label="Nombres y Apellidos"
                field="fullName"
                form={form}
                errors={errors}
                refs={refs}
                onChange={handleChange("fullName", (v) => v.toUpperCase())}
                onKeyDown={handleEnter("fullName")}
              />
            </Grid>

            {/* DOCUMENT TYPE */}
            <Grid item xs={12} sm={6}>
              <FormTextField
                label="Tipo de Documento"
                field="documentType"
                select
                form={form}
                errors={errors}
                refs={refs}
                onChange={handleChange("documentType")}
                onKeyDown={handleEnter("documentType")}
              >
                {_documentTypes.map(([name]) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </FormTextField>
            </Grid>

            {/* DOCUMENT NUMBER */}
            <Grid item xs={12} sm={6}>
              <FormTextField
                label="Número Documento"
                field="documentNumber"
                type="number"
                form={form}
                errors={errors}
                refs={refs}
                onChange={handleChange("documentNumber")}
                onKeyDown={handleEnter("documentNumber")}
              />
            </Grid>

            {/* PERSON TYPE */}
            <Grid item xs={12} sm={6}>
              <FormTextField
                label="Tipo de Persona"
                field="personType"
                select
                form={form}
                errors={errors}
                refs={refs}
                onChange={handleChange("personType")}
                onKeyDown={handleEnter("personType")}
              >
                {_personTypes.map((p) => (
                  <MenuItem key={p.id} value={p.value}>
                    {p.value}
                  </MenuItem>
                ))}
              </FormTextField>
            </Grid>

            {/* PHONE */}
            <Grid item xs={12} sm={6}>
              <FormTextField
                label="Teléfono"
                field="phone"
                form={form}
                type="number"
                errors={errors}
                refs={refs}
                onChange={handleChange("phone")}
                onKeyDown={handleEnter("phone")}
              />
            </Grid>

            {/* EMAIL */}
            <Grid item xs={12} sm={6}>
              <FormTextField
                label="Email"
                field="email"
                form={form}
                errors={errors}
                refs={refs}
                onChange={handleChange("email")}
                onKeyDown={handleEnter("email")}
              />
            </Grid>

            {/* ADDRESS */}
            <Grid item xs={12} sm={6}>
              <FormTextField
                label="Dirección"
                field="address"
                form={form}
                errors={errors}
                refs={refs}
                onChange={handleChange("address", (v) => v.toUpperCase())}
                onKeyDown={handleEnter("address")}
              />
            </Grid>

            {/* DEPARTMENT */}
            <Grid item xs={12} sm={6}>
              <FormTextField
                label="Departamento"
                field="department"
                select
                form={form}
                errors={errors}
                refs={refs}
                onChange={handleChange("department")}
                onKeyDown={handleEnter("department")}
              >
                {_departments.map((d) => (
                  <MenuItem key={d.id} value={d.name}>
                    {d.name}
                  </MenuItem>
                ))}
              </FormTextField>
            </Grid>

            {/* CITY */}
            <Grid item xs={12} sm={6}>
              <FormTextField
                label="Municipio"
                field="city"
                select
                form={form}
                errors={errors}
                refs={refs}
                onChange={handleChange("city")}
                onKeyDown={handleEnter("city")}
              >
                {_municipalities.map((m) => (
                  <MenuItem key={m.id} value={m.name}>
                    {m.name}
                  </MenuItem>
                ))}
              </FormTextField>
            </Grid>

            {/* NEIGHBORHOOD */}
            <Grid item xs={12} sm={6}>
              <FormTextField
                label="Barrio"
                field="neighborhood"
                form={form}
                errors={errors}
                refs={refs}
                onChange={handleChange("neighborhood", (v) => v.toUpperCase())}
                onKeyDown={handleEnter("neighborhood")}
              />
            </Grid>

            {/* NOTES */}
            <Grid item xs={12}>
              <FormTextField
                label="Notas / Observaciones"
                field="notes"
                form={form}
                errors={errors}
                refs={refs}
                onChange={handleChange("notes", (v) => v.toUpperCase())}
                onKeyDown={(e: any) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="contained" type="submit" loading={loading}>
            {initialValue ? "Guardar cambios" : "Crear cliente"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
